import type { Breadcrumb, Contexts, EventHint, SeverityLevel } from "@sentry/vue";
import type * as SentryVue from "@sentry/vue";
import type { App } from "vue";

import { isBrowserTabActive } from "@/utils/dom/dom";
import { asRecord, isNonPrimitive } from "@/utils/record";

import { ApplicationError } from "./ApplicationError";
import { consoleLogBreadcrumbs, consoleSentry } from "./consoleSentry";
import { isReloading, reloadingFor } from "./reload";

type SentryFns = Pick<
  typeof SentryVue,
  | "init"
  | "setUser"
  | "getCurrentScope"
  | "captureException"
  | "captureMessage"
  | "addBreadcrumb"
  | "replayIntegration"
> & { fingerprint: string };

let sentry: SentryFns = consoleSentry;

export async function initConsoleSentry(logBreadcrumbs: boolean) {
  consoleLogBreadcrumbs(logBreadcrumbs);
  return Promise.resolve();
}

export async function initRealSentry(app: App, dsn: string, environment: string) {
  await loadSentry();
  const config: Parameters<typeof SentryVue.init>[0] = {
    app,
    dsn,
    environment,
    normalizeDepth: 6,
    attachStacktrace: true,
    beforeBreadcrumb(breadcrumb) {
      if (breadcrumb.category === "fetch" && breadcrumb.data?.url?.includes("amplitude.com")) {
        return null;
      }
      return breadcrumb;
    },
    replaysSessionSampleRate: 0.0,
    replaysOnErrorSampleRate: 1.0,
    // useCompression: false to avoid CSP problems: https://github.com/getsentry/sentry-javascript/issues/6739
    integrations: [sentry.replayIntegration({ useCompression: false })],
  };
  sentry.init(config);
}

async function loadSentry() {
  try {
    const [sentryVue, { ClientJS }] = await Promise.all([
      import("@sentry/vue"),
      import("clientjs"),
    ]);
    const fingerprint = new ClientJS().getFingerprint().toString(16);
    sentry = { ...sentryVue, fingerprint };
  } catch (_) {
    //ignore and fallback to consoleSentry
  }
}

export function setSentryUser(company: string, authid: string) {
  const scope = sentry.getCurrentScope();
  scope.setUser({ id: sentry.fingerprint, company, username: authid });
  scope.setTag("company", company);
}

export function resetSentryUser() {
  const scope = sentry.getCurrentScope();
  scope.setUser({ id: scope.getUser()?.id });
  scope.setTag("company", undefined);
}

export function captureException(exception: unknown, contexts: Contexts = {}) {
  if (!isReloading()) {
    const error = isNonPrimitive(exception)
      ? exception
      : new Error(`Non-error thrown: ${String(exception)}`);
    const exceptionContexts = exception instanceof ApplicationError ? exception.contexts : {};
    return sentry.captureException(error, eventHint({ ...contexts, ...exceptionContexts }));
  }
}

export function captureMessage(
  message: string,
  contexts: Contexts = {},
  level: SeverityLevel = "error",
) {
  if (!isReloading()) {
    return sentry.captureMessage(message, { level, ...eventHint(contexts).captureContext });
  }
}

export function addBreadcrumb(
  category: Breadcrumb["category"],
  additionalProperties: Breadcrumb = {},
) {
  sentry.addBreadcrumb({ ...additionalProperties, category });
}

function eventHint(contexts: Contexts): EventHint {
  const request = contexts.request;
  const eventId = request?.correlationId || asRecord(request?.headers)?.["correlation-id"];
  const reloading = reloadingFor();
  return {
    event_id: eventId as string | undefined,
    captureContext: {
      contexts: {
        ...contexts,
        browserState: {
          tabActive: isBrowserTabActive(),
          online: navigator.onLine,
          ...(reloading >= 0 ? { reloading: reloading + " ms" } : {}),
        },
      },
    },
  };
}
